import { useState } from 'react';
import { Eye } from 'lucide-react';
import { useCompBands, useOrgPositions, useRevealPayBand, type CompBand, type CompStrategy } from '../../../hooks';
import { useConfirm } from '../../../components/ui/confirm';
import { useToast } from '../../../components/ui/toast';
import { Modal } from '../../../components/ui/Modal';
import { inputCls, btnPrimary, btnGhost, thb, Field, Select, EmptyState, TableShell } from './_shared';

/** ระดับเงินเดือน — per-position min/mid/max band (1 active band per position). */

const STRATEGY_LABEL: Record<CompStrategy, string> = {
  aggressive: 'นำตลาด (จ่ายสูง)',
  fair: 'เท่าตลาด',
  lean: 'ประหยัด (จ่ายต่ำกว่า)',
  custom: 'กำหนดเอง',
};

export function BandsTab() {
  const bands = useCompBands();
  const positions = useOrgPositions();
  const confirm = useConfirm();
  const toast = useToast();
  // เรื่อง 3b — ช่วงเงินเดือนถูกมาส์กสำหรับ HR; กด "ขอดู" เปิดทีละแถว (ระบบบันทึก log)
  const revealBand = useRevealPayBand();
  const [revealed, setRevealed] = useState<Record<string, { min_salary: number; mid_salary: number; max_salary: number }>>({});
  async function onRevealBand(id: string) {
    if (!(await confirm({
      title: 'เปิดดูช่วงเงินเดือน',
      message: 'การเปิดดูนี้จะถูกบันทึกลงในรายการเปิดดูข้อมูลอ่อนไหว (ระบบจะบันทึกว่าใคร เปิดดูของใคร เมื่อไหร่)',
      confirmText: 'เปิดดู',
    }))) return;
    try {
      const r = await revealBand.mutateAsync(id);
      setRevealed((p) => ({ ...p, [id]: { min_salary: r.min_salary, mid_salary: r.mid_salary, max_salary: r.max_salary } }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เปิดดูไม่สำเร็จ');
    }
  }
  const poss = (positions.data ?? []).map((p) => ({ id: p.id, name: `${p.title} (L${p.level})` }));
  const posName = (id: string) => positions.data?.find((p) => p.id === id)?.title ?? '—';
  const rows = bands.data ?? [];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CompBand | null>(null);
  const [positionId, setPositionId] = useState('');
  const [min, setMin] = useState('');
  const [mid, setMid] = useState('');
  const [max, setMax] = useState('');
  const [strategy, setStrategy] = useState<string>('fair');

  function openNew() {
    setEditing(null); setPositionId(''); setMin(''); setMid(''); setMax(''); setStrategy('fair'); setOpen(true);
  }
  function openEdit(b: CompBand) {
    setEditing(b); setPositionId(b.position_id);
    setMin(b.min_salary != null ? String(b.min_salary) : '');
    setMid(b.mid_salary != null ? String(b.mid_salary) : '');
    setMax(b.max_salary != null ? String(b.max_salary) : '');
    setStrategy(b.strategy ?? 'fair'); setOpen(true);
  }
  async function onDelete(b: CompBand) {
    if (!(await confirm({ title: `ลบกรอบเงินเดือนของ "${posName(b.position_id)}"?`, danger: true, confirmText: 'ลบ' }))) return;
    bands.del.mutate(b.id, {
      onSuccess: () => toast.success('ลบแล้ว'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ'),
    });
  }
  function onSubmit() {
    if (!positionId) { toast.error('เลือกตำแหน่ง'); return; }
    const mn = Number(min), md = Number(mid), mx = Number(max);
    if (![mn, md, mx].every((n) => Number.isFinite(n) && n > 0)) { toast.error('กรอกเงินเดือน ต่ำสุด/กลาง/สูงสุด ให้ครบ'); return; }
    if (!(mn <= md && md <= mx)) { toast.error('ต้องเป็น ต่ำสุด ≤ กลาง ≤ สูงสุด'); return; }
    const body = { position_id: positionId, min_salary: mn, mid_salary: md, max_salary: mx, strategy };
    bands.save.mutate(
      { id: editing?.id, body },
      {
        onSuccess: () => { toast.success('บันทึกแล้ว'); setOpen(false); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
      },
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">กรอบเงินเดือน (ต่ำสุด–กลาง–สูงสุด) ของแต่ละตำแหน่ง · 1 ตำแหน่งมี 1 กรอบที่ใช้งาน</p>
        <button onClick={openNew} className={btnPrimary}>+ เพิ่มกรอบ</button>
      </div>

      {bands.isLoading ? (
        <p className="py-6 text-center text-sm text-gray-400">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <EmptyState text="ยังไม่มีกรอบเงินเดือน — กด “เพิ่มกรอบ” เพื่อเริ่ม" />
      ) : (
        <TableShell head={
          <tr>
            <th className="px-3 py-2 font-medium">ตำแหน่ง</th>
            <th className="px-3 py-2 font-medium">ต่ำสุด</th>
            <th className="px-3 py-2 font-medium">กลาง</th>
            <th className="px-3 py-2 font-medium">สูงสุด</th>
            <th className="px-3 py-2 font-medium">กลยุทธ์</th>
            <th className="px-3 py-2"></th>
          </tr>
        }>
          {rows.map((b) => {
            const shown = revealed[b.id];
            const locked = b.pay_masked && !shown;
            const cell = (real: number | null, key: 'min_salary' | 'mid_salary' | 'max_salary') =>
              locked ? '•••' : thb(shown ? shown[key] : real);
            return (
            <tr key={b.id} className="border-t border-gray-100">
              <td className="px-3 py-2 font-medium text-gray-900">{posName(b.position_id)}</td>
              <td className="px-3 py-2 text-gray-500">{cell(b.min_salary, 'min_salary')}</td>
              <td className="px-3 py-2 text-gray-500">{cell(b.mid_salary, 'mid_salary')}</td>
              <td className="px-3 py-2 text-gray-500">
                {cell(b.max_salary, 'max_salary')}
                {locked && (
                  <button
                    onClick={() => void onRevealBand(b.id)}
                    disabled={revealBand.isPending}
                    title="ขอดูช่วงเงินเดือน (บันทึกประวัติการเปิดดู)"
                    className="ml-2 inline-flex items-center gap-1 text-amber-600 hover:underline align-middle"
                  >
                    <Eye className="w-3.5 h-3.5" /> ขอดู
                  </button>
                )}
              </td>
              <td className="px-3 py-2 text-gray-500">{b.strategy ? STRATEGY_LABEL[b.strategy] : '—'}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button onClick={() => openEdit(b)} className="text-teal-700 hover:underline">แก้ไข</button>
                <button onClick={() => void onDelete(b)} className="ml-3 text-red-600 hover:underline">ลบ</button>
              </td>
            </tr>
            );
          })}
        </TableShell>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? 'แก้ไขกรอบเงินเดือน' : 'เพิ่มกรอบเงินเดือน'}>
        <div className="space-y-3">
          <Field label="ตำแหน่ง">
            <Select value={positionId} onChange={setPositionId} options={poss} placeholder="— เลือกตำแหน่ง —" />
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="ต่ำสุด"><input className={inputCls} type="number" value={min} onChange={(e) => setMin(e.target.value)} /></Field>
            <Field label="กลาง"><input className={inputCls} type="number" value={mid} onChange={(e) => setMid(e.target.value)} /></Field>
            <Field label="สูงสุด"><input className={inputCls} type="number" value={max} onChange={(e) => setMax(e.target.value)} /></Field>
          </div>
          <Field label="กลยุทธ์ค่าตอบแทน">
            <select className={inputCls} value={strategy} onChange={(e) => setStrategy(e.target.value)}>
              <option value="fair">เท่าตลาด</option>
              <option value="aggressive">นำตลาด (จ่ายสูง)</option>
              <option value="lean">ประหยัด (จ่ายต่ำกว่า)</option>
              <option value="custom">กำหนดเอง</option>
            </select>
          </Field>
          {editing && <p className="text-xs text-gray-400">การสร้างกรอบใหม่ให้ตำแหน่งเดิมจะเก็บกรอบเก่าเข้าคลังอัตโนมัติ</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setOpen(false)} className={btnGhost}>ยกเลิก</button>
            <button onClick={onSubmit} disabled={bands.save.isPending} className={btnPrimary}>บันทึก</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
